import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { collectionPath, useCollections } from "../api/queries";
import { searchTitles } from "../api/search";
import { useDebounced } from "../lib/useDebounced";
import { Dialog } from "../ui/Dialog";

export interface CommandEntry {
  id: string;
  label: string;
  section: string;
  to: string;
}

/** The fixed entries: navigation and the actions that are just a route. */
export const navigationCommands: CommandEntry[] = [
  { id: "home", label: "Go to Home", section: "Navigation", to: "/home" },
  { id: "drafts", label: "Go to Drafts", section: "Navigation", to: "/drafts" },
  { id: "archive", label: "Go to Archive", section: "Navigation", to: "/archive" },
  { id: "trash", label: "Go to Trash", section: "Navigation", to: "/trash" },
  { id: "search", label: "Search documents", section: "Navigation", to: "/search" },
  { id: "new-doc", label: "New document", section: "Documents", to: "/doc/new" },
  { id: "profile", label: "Open Profile settings", section: "Settings", to: "/settings" },
  {
    id: "preferences",
    label: "Open Preferences",
    section: "Settings",
    to: "/settings/preferences",
  },
  { id: "members", label: "Open Members", section: "Settings", to: "/settings/users" },
  { id: "api-keys", label: "Open API keys", section: "Settings", to: "/settings/api-keys" },
];

/**
 * The command menu (`Mod+K`). Navigation, collections and settings are matched
 * locally; documents come from `documents.search_titles`, which is the
 * autocomplete endpoint — titles only, prefix-matched, no body scan.
 */
export function CommandPalette({
  open,
  onClose,
  entries = navigationCommands,
}: {
  open: boolean;
  onClose: () => void;
  entries?: CommandEntry[];
}): ReactNode {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const debounced = useDebounced(query.trim(), 200);

  const collections = useCollections(open);
  const documents = useQuery({
    queryKey: ["documents.search_titles", debounced],
    enabled: open && debounced !== "",
    retry: false,
    queryFn: () => searchTitles(debounced, 8),
  });

  const local = useMemo(() => {
    const collectionEntries: CommandEntry[] = (collections.data ?? []).map((collection) => ({
      id: `collection:${collection.id}`,
      label: collection.name,
      section: "Collections",
      to: collectionPath(collection),
    }));
    const all = [...entries, ...collectionEntries];
    if (needle === "") return all;
    return all.filter(
      (entry) =>
        entry.label.toLowerCase().includes(needle) || entry.section.toLowerCase().includes(needle),
    );
  }, [collections.data, entries, needle]);

  const found: CommandEntry[] = (documents.data ?? []).map((document) => ({
    id: `document:${document.id}`,
    label: document.title === "" ? "Untitled" : document.title,
    section: "Documents",
    to: document.url,
  }));

  const matches = [...local, ...found];

  const run = (entry: CommandEntry): void => {
    onClose();
    setQuery("");
    void navigate(entry.to);
  };

  return (
    <Dialog open={open} onClose={onClose} title="Command menu" width="md">
      <label className="block">
        <span className="sr-only">Search commands</span>
        <input
          type="search"
          autoFocus
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && matches[0] !== undefined) {
              event.preventDefault();
              run(matches[0]);
            }
          }}
          placeholder="Type a command…"
          className="w-full rounded-md border border-line bg-canvas px-3 py-2 text-sm text-ink"
        />
      </label>

      <ul className="mt-3 max-h-80 overflow-y-auto" aria-label="Commands">
        {matches.length === 0 ? (
          <li className="py-6 text-center text-sm text-muted">Nothing matches “{query}”.</li>
        ) : (
          matches.map((entry) => (
            <li key={entry.id}>
              <button
                type="button"
                onClick={() => {
                  run(entry);
                }}
                className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-hover"
              >
                <span>{entry.label}</span>
                <span className="text-xs text-muted">{entry.section}</span>
              </button>
            </li>
          ))
        )}
      </ul>
    </Dialog>
  );
}
